import { Loader2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { safeReturnTarget, signInUrlReturningTo } from '@/lib/return-target'
import {
  isValidSignupUsername,
  normalizeSignupUsername,
  SIGNUP_USERNAME_PATTERN,
} from '@/lib/signup-username'

export function SignUpPage() {
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const { signUp, isLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const { t } = useI18n()

  const handleSignUp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const normalizedUsername = normalizeSignupUsername(username)
    if (!isValidSignupUsername(normalizedUsername)) {
      toast.error(t('auth.signUp.usernameInvalid'))
      return
    }
    if (password !== passwordConfirmation) {
      toast.error(t('auth.signUp.passwordMismatch'))
      return
    }
    if (password.length < 8) {
      toast.error(t('auth.signUp.passwordTooShort'))
      return
    }
    void signUp({ email, password, username: normalizedUsername })
  }

  return (
    <div className="h-screen pt-16">
      <div className="mx-auto w-full max-w-sm pt-24 md:max-w-md">
        <form onSubmit={handleSignUp}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">
                {t('auth.signUp.title')}
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                {t('auth.signUp.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">{t('auth.signUp.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('auth.signUp.emailPlaceholder')}
                    required
                    onChange={(e) => {
                      setEmail(e.target.value)
                    }}
                    value={email}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="signup-username">
                    {t('auth.signUp.username')}
                  </Label>
                  <Input
                    id="signup-username"
                    type="text"
                    autoComplete="username"
                    aria-describedby="signup-username-hint"
                    minLength={3}
                    maxLength={24}
                    pattern={SIGNUP_USERNAME_PATTERN.source}
                    placeholder={t('auth.signUp.usernamePlaceholder')}
                    required
                    title={t('auth.signUp.usernameInvalid')}
                    onChange={(e) => {
                      setUsername(normalizeSignupUsername(e.target.value))
                    }}
                    value={username}
                  />
                  <p
                    id="signup-username-hint"
                    className="text-xs text-muted-foreground"
                  >
                    {t('auth.signUp.usernameHint')}
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">{t('auth.signUp.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder={t('auth.signUp.passwordPlaceholder')}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password_confirmation">
                    {t('auth.signUp.confirmPassword')}
                  </Label>
                  <Input
                    id="password_confirmation"
                    type="password"
                    value={passwordConfirmation}
                    onChange={e => setPasswordConfirmation(e.target.value)}
                    autoComplete="new-password"
                    placeholder={t('auth.signUp.confirmPasswordPlaceholder')}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    t('auth.signUp.button')
                  )}
                </Button>
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <div className="text-sm text-muted-foreground">
                {t('auth.signUp.hasAccount')}{' '}
                {
                }
                <Link
                  to={signInUrlReturningTo(
                    safeReturnTarget(searchParams.get('redirect'), '/dashboard'),
                  )}
                  className="pl-1 text-primary hover:underline"
                >
                  {t('auth.signUp.signInNow')}
                </Link>
              </div>
            </CardFooter>
          </Card>
        </form>
      </div>
    </div>
  )
}
